import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { getItem, removeItem, setItem } from 'owa-local-storage';

const RECURRING_DEVICE_MIN_DISTINCT_DAYS = 2;

interface DeviceVisits {
    days: number;
    lastDay: string;
}

/** Records distinct browser-local visit days without blocking bootstrap on storage failures. */
export function recordDeviceVisit(): void {
    try {
        if (!isHostAppFeatureEnabled('localCacheAllowed')) {
            return;
        }
        const visits = readDeviceVisits();
        if ((visits?.days ?? 0) >= RECURRING_DEVICE_MIN_DISTINCT_DAYS) {
            return;
        }
        // Use the browser's local calendar day (not UTC) so a visit near UTC midnight can't be
        // miscounted as two distinct days.
        const now = new Date();
        const todayKey = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
        if (visits?.lastDay !== todayKey) {
            writeDeviceVisits({ days: (visits?.days ?? 0) + 1, lastDay: todayKey });
        }
    } catch {
        // Visit recording is best effort.
    }
}

/** Forgets the device history so the device has to become recurring again. */
export function clearDeviceVisits(): void {
    removeItem(self, 'offlineDeviceVisits');
}

export function isRecurringDevice(): boolean {
    const visits = readDeviceVisits();
    return (visits?.days ?? 0) >= RECURRING_DEVICE_MIN_DISTINCT_DAYS;
}

function parseDeviceVisits(raw: string | null): DeviceVisits | undefined {
    try {
        const parsed = raw ? JSON.parse(raw) : undefined;
        if (typeof parsed?.days === 'number' && typeof parsed?.lastDay === 'string') {
            return parsed;
        }
    } catch {
        // Corrupt marker is treated as a new device
    }
    return undefined;
}

function readDeviceVisits(): DeviceVisits | undefined {
    return parseDeviceVisits(getItem(self, 'offlineDeviceVisits'));
}

function writeDeviceVisits(visits: DeviceVisits): void {
    setItem(self, 'offlineDeviceVisits', JSON.stringify(visits));
}
